import type { FetchLaptopsParams } from '@/services/laptops';

// Central query key factory — single source of truth for TanStack Query cache keys
export const queryKeys = {
  customers: {
    all: ['customers'] as const,
    list: (params: { search?: string; page?: number }) =>
      ['customers', 'list', params] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
  },
  suppliers: {
    all: ['suppliers'] as const,
    list: (params: { search?: string; page?: number }) =>
      ['suppliers', 'list', params] as const,
    detail: (id: string) => ['suppliers', 'detail', id] as const,
  },
  laptops: {
    all: ['laptops'] as const,
    list: (params: FetchLaptopsParams) =>
      ['laptops', 'list', params] as const,
    detail: (id: string) => ['laptops', 'detail', id] as const,
    history: (id: string) => ['laptops', 'history', id] as const,
  },
  accessories: {
    all: ['accessories'] as const,
    list: (params: { search?: string; category?: string; lowStockOnly?: boolean; archiveFilter?: 'active' | 'archived' | 'all'; page?: number }) =>
      ['accessories', 'list', params] as const,
    detail: (id: string) => ['accessories', 'detail', id] as const,
  },
  purchases: {
    all: ['purchases'] as const,
    list: (params: { search?: string; supplierId?: string; status?: string; archiveFilter?: 'active' | 'archived' | 'all'; page?: number }) =>
      ['purchases', 'list', params] as const,
    detail: (id: string) => ['purchases', 'detail', id] as const,
    payments: (id: string) => ['purchases', 'payments', id] as const,
  },
  sales: {
    all: ['sales'] as const,
    list: (params: { search?: string; customerId?: string; status?: string; page?: number }) =>
      ['sales', 'list', params] as const,
    detail: (id: string) => ['sales', 'detail', id] as const,
    payments: (id: string) => ['sales', 'payments', id] as const,
  },
  returns: {
    all: ['returns'] as const,
    list: (params: { search?: string; status?: string; page?: number }) =>
      ['returns', 'list', params] as const,
    detail: (id: string) => ['returns', 'detail', id] as const,
  },
  repairs: {
    all: ['repairs'] as const,
    list: (params: { search?: string; status?: string; priority?: string; page?: number }) =>
      ['repairs', 'list', params] as const,
    detail: (id: string) => ['repairs', 'detail', id] as const,
    payments: (id: string) => ['repairs', 'payments', id] as const,
  },
  expenses: {
    all: ['expenses'] as const,
    list: (params: { search?: string; category?: string; vendor?: string; paymentMethod?: string; page?: number }) =>
      ['expenses', 'list', params] as const,
    detail: (id: string) => ['expenses', 'detail', id] as const,
  },
  warranties: {
    all: ['warranties'] as const,
    list: (params: { search?: string; status?: string; page?: number }) =>
      ['warranties', 'list', params] as const,
    detail: (id: string) => ['warranties', 'detail', id] as const,
  },
  audit: {
    all: ['audit'] as const,
    list: (params: { entityType?: string; action?: string; search?: string; page?: number }) =>
      ['audit', 'list', params] as const,
  },
  settings: {
    shop: ['settings', 'shop'] as const,
    users: ['settings', 'users'] as const,
  },
  dashboard: {
    stats: (role: string) => ['dashboard', 'stats', role] as const,
  },
  reports: {
    all: ['reports'] as const,
    pnl: ['reports', 'pnl'] as const,
    stockSummary: ['reports', 'stockSummary'] as const,
    stockAgeing: ['reports', 'stockAgeing'] as const,
    lowStock: ['reports', 'lowStock'] as const,
  },
  publicCatalog: {
    list: (params: { search?: string; type?: string; category?: string; condition?: string; page?: number }) =>
      ['publicCatalog', 'list', params] as const,
    detail: (type: string, id: string) => ['publicCatalog', 'detail', type, id] as const,
  },
} as const;
